import { useState } from "react";
import { Bot, Loader2, Sparkles } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const MODES = [
  ["teach_me", "Teach me"],
  ["explain_simply", "Explain simply"],
  ["hint", "Give me a hint"],
  ["review_answer", "Review my answer"],
  ["real_example", "Show a real example"],
  ["interview_question", "Ask me an interview question"],
];

export const MentorPanel = ({ topic, lessonSlug = "" }) => {
  const [mode, setMode] = useState("teach_me");
  const [context, setContext] = useState("");
  const [answer, setAnswer] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const ask = async () => {
    setBusy(true);
    setError("");
    setAnswer("");
    try {
      const { data } = await api.post("/ai/mentor", { mode, topic, context, lessonSlug });
      setAnswer(data.answer);
    } catch (e) {
      setError(errMsg(e, "AI mentor is unavailable"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-border" data-testid="mentor-panel">
      <div className="flex items-center gap-2 px-5 py-4 border-b border-border">
        <Bot className="h-4 w-4 text-primary" />
        <p className="font-display font-bold tracking-tight">AI Mentor</p>
      </div>
      <div className="p-5 space-y-4">
        <div className="flex flex-wrap gap-1.5">
          {MODES.map(([slug, label]) => (
            <button
              key={slug}
              data-testid={`mentor-mode-${slug}`}
              onClick={() => setMode(slug)}
              className={`px-3 py-1.5 text-xs border transition-colors duration-150 ${
                mode === slug ? "border-primary text-primary bg-primary/5" : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <Textarea
          data-testid="mentor-context"
          rows={3}
          className="rounded-none text-sm"
          placeholder={mode === "review_answer" ? "Paste your answer or config for review…" : "Optional: add context or a specific question"}
          value={context}
          onChange={(e) => setContext(e.target.value)}
        />
        <Button className="rounded-none gap-2" onClick={ask} disabled={busy} data-testid="mentor-ask">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Ask mentor
        </Button>
        {error && <p data-testid="mentor-error" className="text-sm text-destructive">{error}</p>}
        {answer && (
          <div data-testid="mentor-answer" className="border border-border bg-accent/40 p-4 text-sm whitespace-pre-wrap leading-relaxed font-mono">
            {answer}
          </div>
        )}
      </div>
    </div>
  );
};
